import api from './api';

export interface Address {
  fullAddress: string;
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  county?: string;
  neighborhood?: string;
  metroArea?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface DashboardData {
  [key: string]: any;
}

export interface AddressEvaluation {
  _id: string;
  userId: string;
  propertyType?: 'single_family' | 'multifamily';
  address: Address;
  dashboards: {
    size?: DashboardData;
    economy?: DashboardData;
    crime?: DashboardData;
    residents?: DashboardData;
    realEstate?: DashboardData;
    equity?: DashboardData;
    teamBuilding?: DashboardData;
    swings?: DashboardData;
  };
  reportGenerated: boolean;
  reportPath?: string;
  isSaved?: boolean;
  savedAt?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  metadata: {
    evaluationDate: string;
    dataSources: string[];
    version: string;
  };
  createdAt: string;
  updatedAt: string;
}

export const evaluationService = {
  async evaluateAddress(
    address: string,
    propertyType: 'single_family' | 'multifamily' = 'single_family'
  ): Promise<AddressEvaluation> {
    const response = await api.post<AddressEvaluation>('/address/evaluate', {
      address,
      propertyType,
    });
    return response.data;
  },

  async getEvaluations(): Promise<AddressEvaluation[]> {
    const response = await api.get<AddressEvaluation[]>('/address/evaluations');
    return response.data;
  },

  async getEvaluation(id: string): Promise<AddressEvaluation> {
    const response = await api.get<AddressEvaluation>(`/address/evaluations/${id}`);
    return response.data;
  },

  async getDashboard(
    evaluationId: string,
    dashboard: string
  ): Promise<DashboardData> {
    const response = await api.get(`/dashboards/${evaluationId}/${dashboard}`);
    const body = response.data as { data?: DashboardData } & DashboardData;
    return body?.data ?? body;
  },

  async getAllDashboards(evaluationId: string): Promise<AddressEvaluation['dashboards']> {
    // Kept for compatibility with older codepaths.
    // Prefer lazy dashboard loading in EvaluationDetail for faster initial render.
    const response = await api.get(`/dashboards/${evaluationId}`);
    return response.data.dashboards;
  },

  async generateReport(evaluationId: string): Promise<Blob> {
    const response = await api.get(`/reports/${evaluationId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async deleteEvaluation(id: string): Promise<void> {
    await api.delete(`/address/evaluations/${id}`);
  },

  async saveEvaluation(id: string): Promise<AddressEvaluation> {
    const response = await api.post<AddressEvaluation>(`/address/evaluations/${id}/save`);
    return response.data;
  },

  async unsaveEvaluation(id: string): Promise<AddressEvaluation> {
    const response = await api.delete<AddressEvaluation>(`/address/evaluations/${id}/save`);
    return response.data;
  },
};
